import { z } from 'zod';

export const workshopStatusSchema = z.enum(['draft', 'scheduled', 'live', 'completed', 'canceled']);
export type WorkshopStatusValue = z.infer<typeof workshopStatusSchema>;

export const guestSchema = z
  .object({
    name: z.string().min(1).max(100),
    email: z.string().email(),
    companyName: z.string().min(1).max(120),
    companyDomain: z
      .string()
      .max(120)
      .nullable()
      .optional()
      .transform((v) => (v && v.length > 0 ? v.replace(/^https?:\/\//, '').replace(/\/.*$/, '') : null)),
    companyLogoUrl: z.string().url().nullable().optional(),
    companyLogoIncludesName: z.boolean().optional(),
    headshotUrl: z.string().url().nullable().optional(),
  })
  .nullable()
  .optional();
export type GuestInput = z.infer<typeof guestSchema>;

/**
 * Tags are normalized at the schema boundary so every write path (HTTP,
 * direct service calls in tests) stores canonical strings: lowercased,
 * whitespace-collapsed, deduped. Prevents "Growth" + "growth" + " growth "
 * from coexisting as separate tags in the index.
 */
export const tagsSchema = z
  .array(z.string().min(1).max(40))
  .max(6)
  .default([])
  .transform((arr) => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of arr) {
      const norm = raw.trim().toLowerCase().replace(/\s+/g, ' ');
      if (norm.length === 0 || seen.has(norm)) continue;
      seen.add(norm);
      out.push(norm);
    }
    return out;
  });

export const createWorkshopSchema = z.object({
  title: z.string().min(3).max(200),
  description: z.string().max(5000).default(''),
  hostName: z.string().min(1).max(100).default('Colin'),
  hostEmail: z.string().email().nullable().optional(),
  hostHeadshotUrl: z.string().url().nullable().optional(),
  startsAt: z
    .string()
    .datetime({ offset: true })
    .describe('ISO 8601 timestamp with timezone offset'),
  durationMin: z.number().int().min(15).max(480),
  tags: tagsSchema,
  guest: guestSchema,
});
export type CreateWorkshopInput = z.infer<typeof createWorkshopSchema>;

export const updateWorkshopSchema = createWorkshopSchema.partial();
export type UpdateWorkshopInput = z.infer<typeof updateWorkshopSchema>;

export const registerForWorkshopSchema = z.object({
  firstName: z.string().trim().min(1).max(100),
  lastInitial: z
    .string()
    .trim()
    .regex(/^\p{L}$/u, 'Last initial must be one letter')
    .transform((value) => value.toUpperCase()),
});
export type RegisterForWorkshopInput = z.infer<typeof registerForWorkshopSchema>;

export const attachRecordingSchema = z.object({
  recordingPlayUrl: z.string().url(),
});
export type AttachRecordingInput = z.infer<typeof attachRecordingSchema>;

export const adoptZoomMeetingSchema = z.object({
  joinUrl: z.string().trim().url().max(2000),
});
export type AdoptZoomMeetingInput = z.infer<typeof adoptZoomMeetingSchema>;

export const listWorkshopsQuerySchema = z.object({
  status: z.enum(['upcoming', 'past', 'all']).default('upcoming'),
});
export type ListWorkshopsQuery = z.infer<typeof listWorkshopsQuerySchema>;

export const createFeedbackReportSchema = z.object({
  message: z.string().trim().min(3).max(3000),
  contactEmail: z
    .string()
    .trim()
    .email()
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
  pageUrl: z.string().trim().max(1000).nullable().optional(),
  userAgent: z.string().trim().max(1000).nullable().optional(),
  viewport: z.string().trim().max(80).nullable().optional(),
});
export type CreateFeedbackReportInput = z.infer<typeof createFeedbackReportSchema>;

export type FeedbackReportDto = {
  id: string;
  message: string;
  contactEmail: string | null;
  pageUrl: string | null;
  userAgent: string | null;
  viewport: string | null;
  status: string;
  createdAt: string;
};

export type GuestDto = {
  name: string;
  /** Only populated on admin responses. */
  email: string | null;
  companyName: string;
  companyDomain: string | null;
  /** Uploaded logo URL when set; otherwise derived favicon/logo URL for the company domain. */
  companyLogoUrl: string | null;
  /** When true, render companyLogoUrl as a wider wordmark instead of icon + company text. */
  companyLogoIncludesName: boolean;
  headshotUrl: string | null;
  /** Admin-only: timestamp the guest was sent their shared Zoom link + calendar invite. */
  registeredAt: string | null;
};

export function logoUrlForDomain(domain: string | null | undefined): string | null {
  if (!domain) return null;
  return `https://icons.duckduckgo.com/ip3/${encodeURIComponent(domain)}.ico`;
}

export type WorkshopDto = {
  id: string;
  slug: string;
  title: string;
  description: string;
  hostName: string;
  /** Only populated on admin responses. Used to send the host their calendar invite. */
  hostEmail: string | null;
  hostHeadshotUrl: string | null;
  startsAt: string;
  durationMin: number;
  status: WorkshopStatusValue;
  coverImageUrl: string | null;
  /** Only populated on admin responses. General Zoom URL for external listings. */
  zoomJoinUrl: string | null;
  /** True when the Zoom meeting was created and is managed outside this app. */
  zoomManagedExternally: boolean;
  recordingPlayUrl: string | null;
  recordingAvailable: boolean;
  recordingReadyAt: string | null;
  tags: string[];
  guest: GuestDto | null;
};

export type RegistrationConfirmationDto = {
  workshopId: string;
  email: string;
  joinUrl: string | null;
};

export type RegistrationDto = {
  id: string;
  workshopId: string;
  email: string;
  name: string;
  createdAt: string;
  zoomStatus: string;
  /** Stored attendee join URL (admin-only); normally shared by the workshop. */
  zoomJoinUrl: string | null;
};

/**
 * Subscriber-facing "do I have a registration for this workshop?" response.
 * Returned by GET /api/workshops/:slug/my-registration. The endpoint is gated
 * on subscriber identity, so the email match is implicit (it's always the
 * caller's own email).
 */
export type MyRegistrationDto = {
  registration: {
    registeredAt: string;
    joinUrl: string | null;
  } | null;
};
